import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  TextField,
  InputAdornment,
  Button,
  Divider,
  Chip,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import { escapeHtml } from '../../Utils/safeHtml';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  WrapText as WrapTextIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '85vw',
    height: '85vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const TextContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
  flex: 1,
  minHeight: 0,
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  backgroundColor: 'white',
  borderRadius: '8px',
  margin: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    margin: '8px',
  },
}));

const TextArea = styled('pre')(({ theme, lineNumbers }) => ({
  flex: 1,
  minHeight: 0,
  margin: 0,
  padding: lineNumbers ? '20px 20px 20px 60px' : '20px',
  backgroundColor: '#fafafa',
  fontSize: '14px',
  fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
  overflow: 'auto',
  lineHeight: '1.5',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: '#2d3748',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  // always show vertical scrollbar
  overflowY: 'scroll',
  scrollbarWidth: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: lineNumbers ? '16px 16px 16px 48px' : '16px',
    fontSize: '13px',
  },
}));

const LineNumbers = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '50px',
  backgroundColor: '#f0f0f0',
  borderRight: '1px solid #e0e0e0',
  fontSize: '12px',
  fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
  lineHeight: '1.5',
  color: '#666',
  textAlign: 'right',
  padding: '20px 8px',
  userSelect: 'none',
  whiteSpace: 'pre',
  overflow: 'auto',
  zIndex: 2,
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    width: '40px',
    fontSize: '11px',
    padding: '16px 6px',
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  padding: '8px 16px',
  borderTop: '1px solid #e0e0e0',
  fontSize: '12px',
  color: '#666',
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  '& .MuiButton-root': {
    minWidth: 'auto',
    padding: '4px 8px',
    fontSize: '0.75rem',
    textTransform: 'none',
    borderRadius: '4px',
  },
}));

const TextViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: rawTextContent,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.text_missing_source', 'File id or path is required'),
  });
  const textContent = typeof rawTextContent === 'string'
    ? rawTextContent
    : rawTextContent == null
      ? ''
      : JSON.stringify(rawTextContent, null, 2);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [wordWrap, setWordWrap] = useState(true);
  // const [viewMode, setViewMode] = useState('text'); // 'text' or 'csv' (unused)
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const lineNumbersRef = useRef(null);

  // Scroll sync: when text area scrolls, scroll line numbers
  useEffect(() => {
    const textEl = textRef.current;
    const lineEl = lineNumbersRef.current;
    if (!textEl || !lineEl) return;
    const handleScroll = () => {
      lineEl.scrollTop = textEl.scrollTop;
    };
    textEl.addEventListener('scroll', handleScroll);
    return () => {
      textEl.removeEventListener('scroll', handleScroll);
    };
  }, [showLineNumbers, textContent]);

  // Determine file type
  const fileExtension = filename.toLowerCase().split('.').pop();
  const isCSV = fileExtension === 'csv';

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
    }
  }, [open, eventId, filePath, isCSV]);

  const downloadText = () => {
    if (textContent) {
      const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const toggleLineNumbers = () => {
    setShowLineNumbers(!showLineNumbers);
  };

  const toggleWordWrap = () => {
    setWordWrap(!wordWrap);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Highlight search terms
  const highlightText = (text, searchTerm) => {
    const safeText = escapeHtml(text);
    if (!searchTerm) return safeText;

    const safeTerm = escapeHtml(searchTerm);
    const regex = new RegExp(`(${safeTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return safeText.replace(regex, (match) => `<mark style="background-color: #ffeb3b; padding: 0 2px;">${match}</mark>`);
  };

  // Generate line numbers
  const generateLineNumbers = (text) => {
    const lines = text.split('\n');
    return lines.map((_, index) => index + 1).join('\n');
  };

  // Calculate text statistics
  const getTextStats = () => {
    if (!textContent) return { lines: 0, words: 0, characters: 0 };
    
    const lines = textContent.split('\n').length;
    const words = textContent.trim().split(/\s+/).length;
    const characters = textContent.length;
    
    return { lines, words, characters };
  };

  const stats = getTextStats();

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <DescriptionIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.text_viewer_title')} - ${filename}` : t('viewer.text_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadText} 
            disabled={!textContent}
            size="small"
            title={t('viewer.download_text')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ToggleContainer>
            <Button
              variant={showLineNumbers ? 'contained' : 'outlined'}
              onClick={toggleLineNumbers}
              size="small"
              sx={{ 
                backgroundColor: showLineNumbers ? primaryColor : 'transparent',
                borderColor: primaryColor,
                color: showLineNumbers ? 'white' : primaryColor,
                '&:hover': {
                  backgroundColor: showLineNumbers ? '#4a4a9a' : 'rgba(87, 87, 176, 0.05)',
                },
              }}
            >
              Line #
            </Button>
            <Button
              variant={wordWrap ? 'contained' : 'outlined'}
              startIcon={<WrapTextIcon />}
              onClick={toggleWordWrap}
              size="small"
              sx={{ 
                backgroundColor: wordWrap ? primaryColor : 'transparent',
                borderColor: primaryColor,
                color: wordWrap ? 'white' : primaryColor,
                '&:hover': {
                  backgroundColor: wordWrap ? '#4a4a9a' : 'rgba(87, 87, 176, 0.05)',
                },
              }}
            >
              Wrap
            </Button>
          </ToggleContainer>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder={t('viewer.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 200 }}
          />
        </Box>
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <TextContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_text')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_text')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : textContent ? (
            <ContentContainer>
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
                {showLineNumbers && (
                  <LineNumbers ref={lineNumbersRef} style={{ overflowY: 'scroll', scrollbarWidth: 'auto' }}>
                    {generateLineNumbers(textContent)}
                  </LineNumbers>
                )}
                <TextArea
                  ref={textRef}
                  lineNumbers={showLineNumbers}
                  style={{
                    whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                    wordBreak: wordWrap ? 'break-word' : 'normal',
                    overflowY: 'scroll',
                    scrollbarWidth: 'auto',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: highlightText(textContent, searchTerm)
                  }}
                />
              </Box>
              
              <StatsContainer>
                <Chip 
                  label={`${stats.lines} lines`} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '11px' }}
                />
                <Chip 
                  label={`${stats.words} words`} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '11px' }}
                />
                <Chip 
                  label={`${stats.characters} characters`} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '11px' }}
                />
                {isCSV && (
                  <Chip 
                    icon={<TableChartIcon />}
                    label="CSV Format"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '11px' }}
                  />
                )}
              </StatsContainer>
            </ContentContainer>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_text_content')}
              </Typography>
            </Box>
          )}
        </TextContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default TextViewer;
